"use client"

import React, { useState, useRef, useEffect, useLayoutEffect } from "react"
import { createPortal } from "react-dom"
import { ChevronLeft, ChevronRight, ChevronDown, X, Plus, Loader2, Hash, Type } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"
import { useSavedTagsStore } from "@/lib/saved-tags-store"
import { useSettingsStore } from "@/lib/settings-store"
import { useAdStore } from "@/lib/ad-store"
import { getMaxSources } from "@/lib/source-limits"
import { toast } from "sonner"
import { useTranslations } from "next-intl"
import type { BooruSite } from "@/lib/booru-api"
import { useTagTaxonomyStore, type TagCategory } from "@/lib/tag-taxonomy-store"
import { buildAntecedentMap, buildPostCountMap, buildTaxonomyCategoryMap, normalizeSuggestionKey } from "@/lib/booru-tag-suggestions"
import { formatPostCount, orderTagSuggestions } from "@/lib/tag-suggestions"
import { getTagCategoryColor } from "@/lib/tag-categorizer"

interface MiniSearchBarProps {
  isVisible: boolean
  selectedSites: BooruSite[]
  availableSites: BooruSite[]
  onSitesChange: (sites: BooruSite[]) => void
  tags: string[]
  onTagsChange: (tags: string[]) => void
  onCreateSet: (tags: string[]) => void
  handMode: "left" | "right"
  sets: Array<{ id: string; name: string; tags: string[] }>
  activeSetId: string
  onSetChange: (setId: string) => void
  className?: string
}

export function MiniSearchBar({
  isVisible,
  selectedSites,
  availableSites,
  onSitesChange,
  tags,
  onTagsChange,
  onCreateSet,
  handMode,
  sets,
  activeSetId,
  onSetChange,
  className,
}: MiniSearchBarProps) {
  const tFilter = useTranslations("filterPanel")
  const isPremiumUser = useAdStore((s) => s.isPremiumUser)
  const maxSources = getMaxSources(isPremiumUser)
  const [searchInput, setSearchInput] = useState("")
  const [showBooruDropdown, setShowBooruDropdown] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [suggestionCategoryMap, setSuggestionCategoryMap] = useState<Record<string, TagCategory>>({})
  const [suggestionCounts, setSuggestionCounts] = useState<Record<string, number>>({})
  const [suggestionAliases, setSuggestionAliases] = useState<Record<string, string>>({})
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  // The source menu is drawn on <body>, not inside the bar: the bar has its own backdrop-blur,
  // and a backdrop-filter nested inside another one cannot see the page behind it (its blur
  // silently does nothing). Position is computed from the trigger button.
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuPos, setMenuPos] = useState<{ left: number; bottom: number } | null>(null)
  const { addTag, incrementFrequency } = useSavedTagsStore()
  
  const searchMode = useSettingsStore((state) => state.searchMode)
  const setSearchMode = useSettingsStore((state) => state.setSearchMode)
  const tagsScrollableMode = useSettingsStore((state) => state.tagsScrollableMode)

  const activeSet = sets.find((s) => s.id === activeSetId)
  const activeIndex = sets.findIndex((s) => s.id === activeSetId)

  // Fetch real tag suggestions from API
  useEffect(() => {
    if (searchInput.length > 1) {
      setLoadingSuggestions(true)
      const timer = setTimeout(async () => {
        try {
          // Use the first selected site for suggestions
          const site = selectedSites[0] || 'gelbooru'
          const response = await fetch(`/api/booru/tags?q=${encodeURIComponent(searchInput)}&site=${site}&limit=15`)

          if (response.ok) {
            const data = await response.json()
            // Same rule as the main search bar: tags starting with what was typed,
            // exact match first, then A-Z (fetched at 15 so the sort has enough to work with).
            const ordered = orderTagSuggestions(searchInput, data.suggestions || [])
            const filtered = ordered.filter((tag: string) => !tags.includes(tag))
            setSuggestionCategoryMap(buildTaxonomyCategoryMap(data.tagSuggestions))
            setSuggestionCounts(buildPostCountMap(data.tagSuggestions))
            setSuggestionAliases(buildAntecedentMap(data.tagSuggestions))
            setSuggestions(filtered)
            setShowSuggestions(filtered.length > 0)
          } else {
            setSuggestionCategoryMap({}); setSuggestionCounts({}); setSuggestionAliases({})
            setSuggestions([])
            setShowSuggestions(false)
          }
        } catch (error) {
          console.error('[v0] Mini search bar tag suggestions failed:', error)
          setSuggestionCategoryMap({}); setSuggestionCounts({}); setSuggestionAliases({})
          setSuggestions([])
          setShowSuggestions(false)
        } finally {
          setLoadingSuggestions(false)
        }
      }, 300)
      return () => clearTimeout(timer)
    } else {
      setSuggestions([])
      setSuggestionCategoryMap({}); setSuggestionCounts({}); setSuggestionAliases({})
      setShowSuggestions(false)
      setLoadingSuggestions(false)
    }
  }, [searchInput, selectedSites, tags])

  const handleSearch = () => {
    if (searchInput.trim()) {
      const newTags = searchInput.split(/[\s,]+/).filter(tag => tag.trim() && !tags.includes(tag.trim()))
      if (newTags.length > 0) {
        const updatedTags = [...tags, ...newTags]
        onTagsChange(updatedTags)
        
        // Save tags and increment frequency
        newTags.forEach(tag => {
          const cleanTag = normalizeSuggestionKey(tag)
          const category = suggestionCategoryMap[cleanTag]
          if (category) {
            useTagTaxonomyStore.getState().categorizeTag(cleanTag, category, undefined, tag)
          }
          addTag(tag.trim())
          incrementFrequency(tag.trim())
        })
        
        // Auto-create set if this is a new search
        if (tags.length === 0) {
          onCreateSet(updatedTags)
        }
      }
      setSearchInput("")
      setShowSuggestions(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch()
    } else if (e.key === "Escape") {
      setShowSuggestions(false)
    }
  }

  const handleSuggestionClick = (suggestion: string) => {
    const updatedTags = [...tags, suggestion]
    const cleanTag = normalizeSuggestionKey(suggestion)
    const category = suggestionCategoryMap[cleanTag]
    if (category) {
      useTagTaxonomyStore.getState().categorizeTag(cleanTag, category, undefined, suggestion)
    }
    onTagsChange(updatedTags)
    addTag(suggestion)
    incrementFrequency(suggestion)
    setSearchInput("")
    setShowSuggestions(false)
    
    if (tags.length === 0) {
      onCreateSet(updatedTags)
    }
  }

  const removeTag = (tagToRemove: string) => {
    onTagsChange(tags.filter(tag => tag !== tagToRemove))
  }

  const navigateSet = (direction: "prev" | "next") => {
    if (direction === "prev" && activeIndex > 0) {
      onSetChange(sets[activeIndex - 1].id)
    } else if (direction === "next" && activeIndex < sets.length - 1) {
      onSetChange(sets[activeIndex + 1].id)
    }
  }

  const handleSiteToggle = (site: BooruSite, checked: boolean) => {
    if (checked) {
      // This picker had no source cap at all (the Filters panel hard-capped everyone at 3).
      if (selectedSites.length >= maxSources) {
        toast.error(tFilter("sourceLimitReached"), {
          description: tFilter("yourCurrentMembershipTierAllowsUpToMAXSO", { mAXSOURCES: maxSources }),
        })
        return
      }
      onSitesChange([...selectedSites, site])
    } else {
      onSitesChange(selectedSites.filter(s => s !== site))
    }
  }

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (dropdownRef.current && !dropdownRef.current.contains(target) && !menuRef.current?.contains(target)) {
        setShowBooruDropdown(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useLayoutEffect(() => {
    if (!showBooruDropdown) return
    const place = () => {
      const rect = dropdownRef.current?.getBoundingClientRect()
      if (rect) setMenuPos({ left: rect.left, bottom: window.innerHeight - rect.top + 16 })
    }
    place()
    window.addEventListener("resize", place)
    window.addEventListener("scroll", place, true)
    return () => {
      window.removeEventListener("resize", place)
      window.removeEventListener("scroll", place, true)
    }
  }, [showBooruDropdown])

  if (!isVisible) return null

  return (
    <div className={cn("relative", className)}>
      {/* Tag Suggestions */}
      {showSuggestions && (
        <div className="absolute bottom-full left-0 right-0 mb-2 glass glass-denser rounded-md shadow-lg max-h-32 overflow-y-auto z-50">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-primary/20 focus-visible:bg-primary/20 focus-visible:outline-none transition-colors"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <span
                className={cn(
                  "flex-1 truncate",
                  (() => {
                    const category = suggestionCategoryMap[normalizeSuggestionKey(suggestion)]
                    return category && category !== "general" ? getTagCategoryColor(category) : ""
                  })(),
                )}
              >
                {suggestionAliases[normalizeSuggestionKey(suggestion)] && (
                  <span className="text-muted-foreground">
                    {suggestionAliases[normalizeSuggestionKey(suggestion)].replace(/_/g, " ")} →{" "}
                  </span>
                )}
                {suggestion.replace(/_/g, " ")}
              </span>
              {suggestionCounts[normalizeSuggestionKey(suggestion)] !== undefined && (
                <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
                  {formatPostCount(suggestionCounts[normalizeSuggestionKey(suggestion)])}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 p-2 bg-card/95 backdrop-blur-sm border border-border/50 rounded-lg shadow-lg">
        {/* Navigation arrows - always on the right edge */}
        {handMode === "left" && (
          <div className="flex gap-1 flex-shrink-0 order-last ml-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateSet("prev")}
              disabled={activeIndex === 0}
              className="h-7 w-7 p-0"
            >
              <ChevronLeft className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateSet("next")}
              disabled={activeIndex === sets.length - 1}
              className="h-7 w-7 p-0"
            >
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        )}

        {/* Booru selector */}
        <div className="relative flex-shrink-0" ref={dropdownRef}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowBooruDropdown(!showBooruDropdown)}
            className="h-7 px-2 text-xs"
          >
            {selectedSites.length === 1 ? selectedSites[0] : `${selectedSites.length} sites`}
            <ChevronDown className="h-3 w-3 ml-1" />
          </Button>

          {showBooruDropdown &&
            menuPos &&
            createPortal(
              <div
                ref={menuRef}
                style={{ position: "fixed", left: menuPos.left, bottom: menuPos.bottom }}
                className="glass glass-denser rounded-md shadow-lg min-w-48 max-h-48 overflow-y-auto z-[9999]"
              >
              <div className="p-2 space-y-2">
                {availableSites.map((site) => (
                  <div key={site} className="flex items-center space-x-2">
                    <Checkbox
                      id={site}
                      checked={selectedSites.includes(site)}
                      onCheckedChange={(checked) => handleSiteToggle(site, checked as boolean)}
                    />
                    <label htmlFor={site} className="text-sm capitalize cursor-pointer">
                      {site}
                    </label>
                  </div>
                ))}
              </div>
              </div>,
              document.body,
            )}
        </div>

        {/* Search Mode Toggle */}
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 hover:bg-muted flex-shrink-0"
          onClick={() => setSearchMode(searchMode === "tags" ? "title" : "tags")}
          title={searchMode === "tags" ? "Switch to title search" : "Switch to tag search"}
        >
          {searchMode === "tags" ? (
            <Hash className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <Type className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </Button>
          
        {/* Search input - stays in fixed position */}
        <div className="flex-shrink-0 w-[180px] relative">
          <Input
            ref={inputRef}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={searchMode === "tags" ? "Add tags..." : "Search by title..."}
            className="h-7 text-xs pr-8"
          />
          {loadingSuggestions ? (
            <Loader2 className="absolute right-1 top-0.5 h-6 w-6 p-1 animate-spin text-muted-foreground" />
          ) : searchInput && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSearch}
              className="absolute right-1 top-0.5 h-6 w-6 p-0"
            >
              <Plus className="h-3 w-3" />
            </Button>
          )}
        </div>

        {/* Tags container - scrollable or wrapping based on setting */}
        {tags.length > 0 && (
          <div 
            className={cn(
              "flex items-center gap-1 relative [&::-webkit-scrollbar]:hidden",
              tagsScrollableMode 
                ? "overflow-x-auto flex-nowrap flex-1" 
                : "flex-wrap flex-1"
            )}
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
            onWheel={(e) => {
              if (tagsScrollableMode) {
                e.currentTarget.scrollLeft += e.deltaY
              }
            }}
          >
            {/* Left fade overlay */}
            {tagsScrollableMode && (
              <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-card/95 to-transparent pointer-events-none z-10" />
            )}
            
            {tags.map((tag) => (
              <Badge
                key={tag}
                variant="secondary"
                className="text-xs flex items-center gap-1 flex-shrink-0 relative z-0"
              >
                {tag.replace(/_/g, " ")}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeTag(tag)}
                  className="h-3 w-3 p-0 hover:bg-destructive hover:text-destructive-foreground"
                >
                  <X className="h-2 w-2" />
                </Button>
              </Badge>
            ))}
            
            {/* Right fade overlay */}
            {tagsScrollableMode && (
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-card/95 to-transparent pointer-events-none z-10" />
            )}
          </div>
        )}

        {/* Navigation arrows - always on the right edge */}
        {handMode === "right" && (
          <div className="flex gap-1 flex-shrink-0 order-last ml-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateSet("prev")}
              disabled={activeIndex === 0}
              className="h-7 w-7 p-0"
            >
              <ChevronLeft className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateSet("next")}
              disabled={activeIndex === sets.length - 1}
              className="h-7 w-7 p-0"
            >
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
